import React, { useState, useMemo } from "react";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Legend,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
} from "recharts";

const StatisticsTab = ({ appEvents, questionStats, questions, gamesTrend }) => {
    const [timeRange, setTimeRange] = useState(30); // Default to 30 days
    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState("all");
    const [activeEventToggles, setActiveEventToggles] = useState({});

    // Get all unique event types
    const allEventTypes = useMemo(() => {
        const types = new Set(appEvents.map((e) => e.type));
        return Array.from(types).sort();
    }, [appEvents]);

    // Initial state for toggles if not set
    useMemo(() => {
        if (Object.keys(activeEventToggles).length === 0 && allEventTypes.length > 0) {
            const initialToggles = {};
            allEventTypes.forEach((type) => {
                initialToggles[type] = true;
            });
            setActiveEventToggles(initialToggles);
        }
    }, [allEventTypes]);

    // Filter events by time range
    const filteredEvents = useMemo(() => {
        const now = new Date();
        const cutoff = new Date(now.getTime() - timeRange * 24 * 60 * 60 * 1000);
        return appEvents.filter((e) => new Date(e.timestamp) >= cutoff);
    }, [appEvents, timeRange]);

    // Prepare timeline data
    const timelineData = useMemo(() => {
        const data = {};
        const now = new Date();

        // Initialize days
        for (let i = timeRange - 1; i >= 0; i--) {
            const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
            const dateStr = d.toISOString().split("T")[0];
            const displayDate = d.toLocaleDateString("he-IL", { day: '2-digit', month: '2-digit' });
            data[dateStr] = { name: displayDate };
            allEventTypes.forEach(type => {
                data[dateStr][type] = 0;
            });
        }

        filteredEvents.forEach((e) => {
            const dateStr = new Date(e.timestamp).toISOString().split("T")[0];
            if (data[dateStr] && e.type) {
                data[dateStr][e.type]++;
            }
        });

        return Object.values(data);
    }, [filteredEvents, timeRange, allEventTypes]);

    // Table filtering
    const tableData = useMemo(() => {
        return appEvents
            .filter((e) => {
                const searchLower = searchQuery.toLowerCase();

                // Extract details string for searching
                const detailsValues = Object.entries(e)
                    .filter(([k]) => !["id", "type", "userId", "username", "timestamp"].includes(k))
                    .map(([_, v]) => v?.toString().toLowerCase())
                    .join(" ");

                const matchesSearch =
                    e.type?.toLowerCase().includes(searchLower) ||
                    e.username?.toLowerCase().includes(searchLower) ||
                    e.userId?.toLowerCase().includes(searchLower) ||
                    detailsValues.includes(searchLower);

                const matchesType = filterType === "all" || e.type === filterType;

                return matchesSearch && matchesType;
            })
            .slice(0, 500); // Limit to 500 for performance
    }, [appEvents, searchQuery, filterType]);

    const EVENT_COLORS = {
        app_open: "#10b981",
        app_close: "#ef4444",
        single_game_start: "#3b82f6",
        friend_add: "#8b5cf6",
        friend_remove: "#f59e0b",
        profile_update: "#ec4899",
        mp_lobby_create: "#06b6d4",
        mp_lobby_join: "#f97316",
        mp_game_start: "#14b8a6",
        premium_purchase: "#f5c518",   // gold — premium purchase
        ad_watch: "#94a3b8",
    };

    const getEventColor = (type) => EVENT_COLORS[type] || "#64748b";

    // Migrated Question Stats Logic
    const { generalStats, harryPotterStats } = questionStats;

    const chartData = [
        {
            name: "Correct Answers",
            GENERAL: generalStats?.correct || 0,
            HARRY_POTTER: harryPotterStats?.correct || 0,
        },
        {
            name: "Wrong Answers",
            GENERAL: generalStats?.wrong || 0,
            HARRY_POTTER: harryPotterStats?.wrong || 0,
        },
    ];

    const difficultyData = questions.reduce((acc, q) => {
        const difficulty = q.difficulty || "Unknown";
        if (!acc[difficulty]) acc[difficulty] = 0;
        acc[difficulty]++;
        return acc;
    }, {});

    const difficultyChartData = Object.keys(difficultyData).map((key) => ({
        difficulty: key,
        count: difficultyData[key],
    }));

    const categoryDistributionData = [
        { name: "General", value: generalStats?.total || 0 },
        { name: "Harry Potter", value: harryPotterStats?.total || 0 },
    ].filter(d => d.value > 0);

    const CATEGORY_COLORS = ["#8884d8", "#82ca9d"];

    return (
        <div className="statistics-tab">
            {/* Timeline Controls */}
            <div className="chart-card" style={{ marginBottom: "2rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                    <h2 style={{ display: "flex", alignItems: "center", gap: "0.5rem", margin: 0 }}>
                        <ion-icon name="analytics-outline" style={{ color: "#3b82f6" }}></ion-icon>
                        Timeline Analytics
                    </h2>
                    <select
                        value={timeRange}
                        onChange={(e) => setTimeRange(Number(e.target.value))}
                        className="time-select"
                        style={{ padding: '0.5rem', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white' }}
                    >
                        <option value={7}>Last 7 Days</option>
                        <option value={30}>Last 30 Days</option>
                        <option value={90}>Last 90 Days</option>
                    </select>
                </div>

                {/* Event Toggles */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginBottom: "1.5rem" }}>
                    {allEventTypes.map(type => (
                        <button
                            key={type}
                            onClick={() => setActiveEventToggles(prev => ({ ...prev, [type]: !prev[type] }))}
                            style={{
                                padding: "0.4rem 0.8rem",
                                borderRadius: "20px",
                                border: "none",
                                fontSize: "0.85rem",
                                cursor: "pointer",
                                backgroundColor: activeEventToggles[type] ? getEventColor(type) : "#f1f5f9",
                                color: activeEventToggles[type] ? "white" : "#64748b",
                                transition: "all 0.2s",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.4rem"
                            }}
                        >
                            <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: activeEventToggles[type] ? "white" : getEventColor(type) }}></div>
                            {type.replace(/_/g, " ")}
                        </button>
                    ))}
                </div>

                <div style={{ height: "400px", width: "100%" }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={timelineData}>
                            <defs>
                                {allEventTypes.map(type => (
                                    <linearGradient key={`grad-${type}`} id={`color-${type}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={getEventColor(type)} stopOpacity={0.1} />
                                        <stop offset="95%" stopColor={getEventColor(type)} stopOpacity={0} />
                                    </linearGradient>
                                ))}
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                            <RechartsTooltip
                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                            />
                            <Legend iconType="circle" />
                            {allEventTypes.map(type => (
                                activeEventToggles[type] && (
                                    <Area
                                        key={type}
                                        type="monotone"
                                        dataKey={type}
                                        stroke={getEventColor(type)}
                                        fillOpacity={1}
                                        fill={`url(#color-${type})`}
                                        strokeWidth={2}
                                        stackId="1"
                                    />
                                )
                            ))}
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Question Statistics (Migrated) */}
            <div className="chart-container-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginBottom: '2rem' }}>
                <div className="chart-card" style={{ marginBottom: 0 }}>
                    <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <ion-icon name="pie-chart-outline" style={{ color: '#8884d8' }}></ion-icon>
                        Category Distribution
                    </h2>
                    <div style={{ height: '300px', width: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={categoryDistributionData}
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {categoryDistributionData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                                    ))}
                                </Pie>
                                <RechartsTooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="chart-card" style={{ marginBottom: 0 }}>
                    <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <ion-icon name="stats-chart-outline" style={{ color: '#8884d8' }}></ion-icon>
                        Answer Accuracy
                    </h2>
                    <div style={{ height: '300px', width: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
                                <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                                <RechartsTooltip contentStyle={{ borderRadius: '12px', border: 'none' }} />
                                <Legend verticalAlign="top" height={36} align="right" iconType="circle" />
                                <Bar dataKey="GENERAL" fill="#8884d8" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="HARRY_POTTER" fill="#82ca9d" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="chart-card" style={{ marginBottom: 0 }}>
                    <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <ion-icon name="layers-outline" style={{ color: '#ffc658' }}></ion-icon>
                        Difficulty Distribution
                    </h2>
                    <div style={{ height: '300px', width: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={difficultyChartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
                                <XAxis dataKey="difficulty" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                                <RechartsTooltip contentStyle={{ borderRadius: '12px', border: 'none' }} />
                                <Bar dataKey="count" fill="#ffc658" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Events Table */}
            <div className="block" style={{ marginTop: "2rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                    <h1 style={{ margin: 0 }}>Recent Events Log</h1>
                    <div style={{ display: "flex", gap: "1rem" }}>
                        <div style={{ position: "relative" }}>
                            <ion-icon name="search-outline" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}></ion-icon>
                            <input
                                type="text"
                                placeholder="Search events, users, details..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ padding: "0.6rem 1rem 0.6rem 2.5rem", borderRadius: "10px", border: "1px solid #e2e8f0", width: "250px" }}
                            />
                        </div>
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            style={{ padding: "0.6rem 1rem", borderRadius: "10px", border: "1px solid #e2e8f0", background: "white", color: "#475569", fontWeight: "600" }}
                        >
                            <option value="all">All Event Types</option>
                            {allEventTypes.map(type => (
                                <option key={type} value={type}>{type.replace(/_/g, " ").toUpperCase()}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="blockTableContainer">
                    <table className="blockTable">
                        <thead>
                            <tr>
                                <th>Timestamp</th>
                                <th>User</th>
                                <th>Event Type</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.map((e) => (
                                <tr key={e.id}>
                                    <td>{e.timestamp.toLocaleString("he-IL")}</td>
                                    <td>
                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                            <span style={{ fontWeight: 600 }}>{e.username}</span>
                                            <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>{e.userId}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span style={{
                                            padding: "0.25rem 0.6rem",
                                            borderRadius: "12px",
                                            fontSize: "0.75rem",
                                            fontWeight: 600,
                                            backgroundColor: getEventColor(e.type) + "15",
                                            color: getEventColor(e.type)
                                        }}>
                                            {e.type?.replace(/_/g, " ").toUpperCase()}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ fontSize: "0.85rem", color: "#475569" }}>
                                            {Object.entries(e)
                                                .filter(([k]) => !["id", "type", "userId", "username", "timestamp"].includes(k))
                                                .map(([k, v]) => (
                                                    <div key={k}><b>{k}:</b> {v?.toString()}</div>
                                                ))
                                            }
                                            {Object.keys(e).length <= 5 && <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>No extra details</span>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default StatisticsTab;
